<template>
  <div class="ten-date-range">
    <div class="ten-date">
      <ten-date-header
        :year="leftYear"
        :month="leftMonth"
        :type.sync="leftType"
        @btn-click="(a, b) => onHeaderClick(a, b, LEFT)"
        @update:type="onTypeChange"
      />

      <ten-date-table
        :type="leftType"
        :first-day-of-week="firstDayOfWeek"
        :data="leftData"
        :on-item-click="getClickHandler(LEFT)"
        :on-item-mouse-enter="onMouseEnter"
      />
    </div>
    <div class="ten-date">
      <ten-date-header
        :year="rightYear"
        :month="rightMonth"
        :type.sync="rightType"
        @btn-click="(a, b) => onHeaderClick(a, b, RIGHT)"
        @update:type="onTypeChange"
      />

      <ten-date-table
        :type="rightType"
        :first-day-of-week="firstDayOfWeek"
        :data="rightData"
        :on-item-click="getClickHandler(RIGHT)"
        :on-item-mouse-enter="onMouseEnter"
      />
    </div>
  </div>
</template>

<script>
import TenDateHeader from '../basic/header';
import TenDateTable from '../basic/table';

import {
  getWeeks,
  getYears,
  getMonths,
  flagActive,
  subtractMonth,
  addMonth,
  isSame,
  getToday,
  firstUpperCase,
  setDateTime,
} from '../util';

const TODAY = getToday();

export default {
  name: 'TenDatePickerDateRange',
  inject: {
    picker: {
      from: 'picker',
      default: undefined,
    },
  },
  components: {
    TenDateHeader,
    TenDateTable,
  },
  inheritAttrs: false,
  props: {
    mode: {
      type: String,
      default: 'date',
      validator: v => ['year', 'month', 'date'].indexOf(v) > -1,
    },
    value: {
      type: Array,
      default: () => [TODAY, TODAY],
    },
    minDate: Date,
    maxDate: Date,
    firstDayOfWeek: Number,
    disabledDate: Function,
  },
  data() {
    return {
      ...this.getLeftAndRightDataFromValue(this.value),
      leftType: this.mode,
      rightType: this.mode,
      startValue: null,
      endValue: null,
    };
  },
  computed: {
    leftData() {
      return this.getData({
        year: this.leftYear,
        month: this.leftMonth,
        type: this.leftType,
      });
    },
    rightData() {
      return this.getData({
        year: this.rightYear,
        month: this.rightMonth,
        type: this.rightType,
      });
    },
  },
  watch: {
    value: {
      handler(value) {
        const [startValue = TODAY, endValue = TODAY] = value;
        this.startValue = startValue;
        this.endValue = endValue;
      },
      immediate: true,
    },
    mode(value) {
      this.leftType = value;
      this.rightType = value;
    },
  },
  beforeCreate() {
    this.LEFT = 'left';
    this.RIGHT = 'right';
    this.isFirstClick = true;
    this.firstClickValue = TODAY;
  },
  created() {
    this.picker && this.picker.$on('popupShowChange', this.initialPicker);
  },
  beforeDestroy() {
    this.picker && this.picker.$off('popupShowChange', this.initialPicker);
  },
  methods: {
    initialPicker(popupShow) {
      if (!popupShow) return;
      const data = this.getLeftAndRightDataFromValue(this.value);

      this.leftYear = data.leftYear;
      this.leftMonth = data.leftMonth;
      this.rightYear = data.rightYear;
      this.rightMonth = data.rightMonth;
    },
    getLeftAndRightDataFromValue(value) {
      const [startValue = TODAY, endValue = TODAY] = value || this.value;
      const leftYear = startValue.getFullYear();
      const leftMonth = startValue.getMonth();
      let rightMonth = endValue.getMonth();
      let rightYear = endValue.getFullYear();

      if (this.mode === 'date' && isSame(startValue, endValue, 'month')) {
        const next = addMonth(endValue, 1);
        rightMonth = new Date(endValue).getMonth() + 1;
        rightYear = next.getFullYear();
      }

      if (this.mode === 'month' && isSame(startValue, endValue, 'year')) {
        rightYear = leftYear + 1;
      }

      if (this.mode === 'year' && isSame(startValue, endValue, 'year')) {
        rightYear = leftYear + 10;
      }

      return {
        leftYear,
        leftMonth,
        rightMonth,
        rightYear,
      };
    },
    getData({ year, month, type }) {
      const { disabledDate, minDate, maxDate, firstDayOfWeek, startValue, endValue } = this;
      let data;

      const start = startValue;
      const end = endValue;

      const options = {
        disabledDate,
        minDate,
        maxDate,
        firstDayOfWeek,
      };

      switch (type) {
        case 'date':
          data = getWeeks({ year, month }, options);
          break;
        case 'month':
          data = getMonths(year, options);
          break;
        case 'year':
          data = getYears(year, options);
          break;
        default:
          break;
      }

      return flagActive(data, { start, end, type });
    },
    getClickHandler(direction) {
      const type = this[`${direction}Type`];
      return date => this[`click${firstUpperCase(type)}`](date, direction);
    },
    onHeaderClick(btnType, flag) {
      this.clickHeader(btnType, flag, this.LEFT);
      this.clickHeader(btnType, flag, this.RIGHT);
    },
    clickHeader(btnType, flag, direction) {
      const year = this[`${direction}Year`];
      const month = this[`${direction}Month`];
      const type = this[`${direction}Type`];

      let monthCount; let next;
      switch (type) {
        case 'date':
          monthCount = btnType === 'double' ? 12 : 1;
          break;
        case 'month':
          monthCount = btnType === 'double' ? 60 : 12;
          break;
        case 'year':
          monthCount = btnType === 'double' ? 240 : 120;
      }

      const current = new Date(year, month);

      if (flag === 1) {
        next = addMonth(current, monthCount);
      } else {
        next = subtractMonth(current, monthCount);
      }

      this[`${direction}Year`] = next.getFullYear();
      this[`${direction}Month`] = next.getMonth();
    },
    clickDate(date) {
      if (this.isFirstClick) {
        this.startValue = date;
        this.endValue = date;
        this.isFirstClick = false;
        this.firstClickValue = date;
      } else {
        this.$emit('change', [this.startValue, setDateTime(this.endValue, 23, 59, 59)]);
        this.isFirstClick = true;
      }
    },
    clickYear(date, type) {
      if (this.mode === 'year') {
        if (this.isFirstClick) {
          this.startValue = date;
          this.isFirstClick = false;
          this.firstClickValue = date;
        } else {
          this.$emit('change', [this.startValue, this.endValue]);
          this.isFirstClick = true;
        }
      } else {
        this[`${type}Type`] = 'month';
        this[`${type}Year`] = date.getFullYear();
      }
    },
    clickMonth(date, type) {
      if (this.mode === 'month') {
        if (this.isFirstClick) {
          this.startValue = date;
          this.isFirstClick = false;
          this.firstClickValue = date;
        } else {
          if (this.endValue < this.startValue) {
            this.endValue = this.startValue;
          }
          this.$emit('change', [this.startValue, this.endValue]);
          this.isFirstClick = true;
        }
      } else {
        this[`${type}Type`] = 'date';
        this[`${type}Month`] = date.getMonth();
        this[`${type}Year`] = date.getFullYear();
      }
    },
    onMouseEnter(date) {
      if (this.isFirstClick) {
        return;
      }

      if (this.firstClickValue.getTime() > date.getTime()) {
        this.startValue = date;
        this.endValue = this.firstClickValue;
      } else {
        this.startValue = this.firstClickValue;
        this.endValue = date;
      }
    },
    onTypeChange() {
      this.startValue = this.firstClickValue;
      this.endValue = this.firstClickValue;
    },
  },
};
</script>
